import { useCallback } from 'react';

import { useNavigate } from 'react-router-dom';

import { CAST_AI } from '@cast/constants';
import {
  Button,
  DrawerProvider,
  HexIcon,
  Icons,
  Tooltip,
  Typography,
  Stack,
} from '@cast/design-system';
import {
  CLUSTER_DASHBOARD_TOOLTIPS,
  ClusterDashboardTimeRangeProvider,
  useClusterDashboardDelete,
  useClusterDashboardState,
} from '@cast/features-cluster-shared';
import { Page } from '@cast/shared-layout-ui';

import { useCastctlOnboardingDialog } from '@cast/split-me/hooks/dialogs';

import { ClusterMetadataBar } from '@oneclick/features/cluster-dashboard';

export const InaccessibleClusterDashboardPage = () => {
  const navigate = useNavigate();
  const { openCastctlOnboardingDialog } = useCastctlOnboardingDialog();
  const { cluster, isTerraformManaged } = useClusterDashboardState();
  const { deleteCluster, isDeleting } = useClusterDashboardDelete(cluster, {
    onDeleted: () => navigate('/clusters'),
  });

  const handleReconnect = useCallback(() => {
    openCastctlOnboardingDialog({
      cluster,
      skipConnectionMonitoring: true,
    });
  }, [cluster, openCastctlOnboardingDialog]);

  return (
    <ClusterDashboardTimeRangeProvider>
      <DrawerProvider>
        <Page
          analytics="Inaccessible Cluster Dashboard"
          testId="inaccessible-cluster-dashboard"
        >
          <Stack gap={24} alignItems="center" maxWidth={640} mx="auto" py={48}>
            <HexIcon
              variant="error"
              size={64}
              icon={<Icons.XCircle weight="fill" />}
            />
            <Stack gap={8} textAlign="center">
              <Typography variant="h5">
                Cluster {cluster.name} is no longer accessible
              </Typography>
              <Typography variant="P14R" color="text.secondary">
                Your cluster is no longer connected to {CAST_AI}. Please keep
                your cluster list updated by completing any action below.
              </Typography>
            </Stack>
            <Stack direction="row" gap={16}>
              <Tooltip
                title={CLUSTER_DASHBOARD_TOOLTIPS.actionRequiresTerraform}
                wrapIf={isTerraformManaged}
              >
                <span>
                  <Button
                    variant="primary"
                    onClick={handleReconnect}
                    disabled={isTerraformManaged}
                  >
                    Reconnect
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="danger"
                onClick={deleteCluster}
                loading={isDeleting}
                testId="remove-cluster-button"
              >
                Remove cluster
              </Button>
            </Stack>
            <Stack width="100%">
              <ClusterMetadataBar />
            </Stack>
          </Stack>
        </Page>
      </DrawerProvider>
    </ClusterDashboardTimeRangeProvider>
  );
};
